import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Autoscaling console (part 17).
 *
 * The page separates the two halves of the feature on purpose: evaluating a policy produces a
 * recommendation from real telemetry, and only an explicit owner button starts or stops the extra
 * process. The recommendation card says so in Thai, and the worker table shows the pid and log of
 * the one process on port 8899. The baseline API on 8787/8790 is not represented here because
 * this feature cannot touch it.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

interface ScalingPolicy {
  id: string
  name: string
  service: string
  min_replicas: number
  max_replicas: number
  target_cpu_pct: number
  cpu_scale_out_pct: number
  cpu_scale_in_pct: number
  target_p95_ms: number
  p95_scale_out_ms: number
  p95_scale_in_ms: number
  latency_route: string
  sample_window_seconds: number
  min_samples: number
  cooldown_seconds: number
  enabled: boolean
  current_replicas: number
  live_workers: number
  last_evaluated_at?: string
  last_decision_at?: string
  version: number
  created_at: string
  updated_at: string
}

interface ScalingDecision {
  id: string
  policy_id: string
  policy_name?: string
  source: 'evaluation' | 'owner'
  action: 'scale_out' | 'scale_in' | 'hold'
  would_action: 'scale_out' | 'scale_in' | 'hold'
  reason: string
  suppressed: boolean
  applied: boolean
  current_replicas: number
  desired_replicas: number
  target_replicas: number
  cpu_pct: number
  cpu_samples: number
  p95_latency_ms: number
  latency_samples: number
  latency_available: boolean
  cpu_threshold_pct: number
  p95_threshold_ms: number
  cooldown_seconds: number
  cooldown_remaining_seconds: number
  note: string
  decided_at: string
}

interface ScalingWorker {
  id: string
  policy_id: string
  name: string
  port: number
  pid: number
  log_path: string
  status: 'starting' | 'running' | 'stopped' | 'failed'
  last_error: string
  started_at?: string
  stopped_at?: string
  created_at: string
}

interface AutoscaleOverview {
  policies: ScalingPolicy[]
  decisions: ScalingDecision[]
  workers: ScalingWorker[]
  baseline_replicas: number
  worker_port: number
  worker_script?: string
  worker_script_ready: boolean
  note: string
}

interface ScalingEvaluation {
  policy: ScalingPolicy
  observation: {
    cpu_pct: number
    cpu_samples: number
    p95_latency_ms: number
    latency_samples: number
    window_started_at: string
    window_ended_at: string
    sample_id?: string
  }
  decision: ScalingDecision
  recommendation: string
  worker_started: boolean
}

interface PolicyForm {
  name: string
  service: string
  min_replicas: string
  max_replicas: string
  target_cpu_pct: string
  cpu_scale_out_pct: string
  cpu_scale_in_pct: string
  target_p95_ms: string
  p95_scale_out_ms: string
  p95_scale_in_ms: string
  latency_route: string
  sample_window_seconds: string
  min_samples: string
  cooldown_seconds: string
  enabled: boolean
}

const OVERVIEW_KEY = ['autoscale-overview']

const REASON_LABEL: Record<string, string> = {
  cpu_threshold: 'CPU สูงกว่าเกณฑ์ขยาย',
  latency_threshold: 'p95 สูงกว่าเกณฑ์ขยาย',
  cpu_and_latency_threshold: 'CPU และ p95 สูงกว่าเกณฑ์',
  below_min_replicas: 'ต่ำกว่าจำนวนขั้นต่ำ',
  below_scale_in_threshold: 'ต่ำกว่าเกณฑ์ลดจำนวน',
  above_max_replicas: 'สูงกว่าจำนวนสูงสุด',
  cooldown_active: 'อยู่ในช่วง cooldown',
  at_max_replicas: 'ถึงจำนวนสูงสุดแล้ว',
  at_min_replicas: 'ถึงจำนวนต่ำสุดแล้ว',
  insufficient_samples: 'ตัวอย่างไม่เพียงพอ',
  policy_disabled: 'ปิดใช้งานนโยบาย',
  within_hysteresis_band: 'อยู่ในช่วงกันสั่น',
  target_met: 'จำนวนเหมาะกับภาระแล้ว',
  owner_requested: 'เจ้าของสั่งดำเนินการ',
}

const ACTION_LABEL: Record<string, string> = {
  scale_out: 'ขยาย',
  scale_in: 'ลด',
  hold: 'คงไว้',
}

const WORKER_LABEL: Record<ScalingWorker['status'], string> = {
  starting: 'กำลังเริ่ม',
  running: 'ทำงานอยู่',
  stopped: 'หยุดแล้ว',
  failed: 'ล้มเหลว',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function actionTone(action: string): string {
  if (action === 'scale_out') return 'badge badge-received'
  if (action === 'scale_in') return 'badge badge-reviewing'
  return 'chip'
}

function workerTone(status: ScalingWorker['status']): string {
  if (status === 'running') return 'sev sev-low'
  if (status === 'starting') return 'sev sev-medium'
  if (status === 'failed') return 'sev sev-critical'
  return 'sev sev-low'
}

function defaultForm(): PolicyForm {
  return {
    name: 'core-api-autoscale',
    service: 'core-api',
    min_replicas: '1',
    max_replicas: '2',
    target_cpu_pct: '60',
    cpu_scale_out_pct: '70',
    cpu_scale_in_pct: '35',
    target_p95_ms: '400',
    p95_scale_out_ms: '750',
    p95_scale_in_ms: '200',
    latency_route: '',
    sample_window_seconds: '600',
    min_samples: '3',
    cooldown_seconds: '300',
    enabled: true,
  }
}

function formFromPolicy(p: ScalingPolicy): PolicyForm {
  return {
    name: p.name,
    service: p.service,
    min_replicas: String(p.min_replicas),
    max_replicas: String(p.max_replicas),
    target_cpu_pct: String(p.target_cpu_pct),
    cpu_scale_out_pct: String(p.cpu_scale_out_pct),
    cpu_scale_in_pct: String(p.cpu_scale_in_pct),
    target_p95_ms: String(p.target_p95_ms),
    p95_scale_out_ms: String(p.p95_scale_out_ms),
    p95_scale_in_ms: String(p.p95_scale_in_ms),
    latency_route: p.latency_route,
    sample_window_seconds: String(p.sample_window_seconds),
    min_samples: String(p.min_samples),
    cooldown_seconds: String(p.cooldown_seconds),
    enabled: p.enabled,
  }
}

function payload(form: PolicyForm): Record<string, unknown> {
  return {
    name: form.name,
    service: form.service,
    min_replicas: Number(form.min_replicas),
    max_replicas: Number(form.max_replicas),
    target_cpu_pct: Number(form.target_cpu_pct),
    cpu_scale_out_pct: Number(form.cpu_scale_out_pct),
    cpu_scale_in_pct: Number(form.cpu_scale_in_pct),
    target_p95_ms: Number(form.target_p95_ms),
    p95_scale_out_ms: Number(form.p95_scale_out_ms),
    p95_scale_in_ms: Number(form.p95_scale_in_ms),
    latency_route: form.latency_route.trim(),
    sample_window_seconds: Number(form.sample_window_seconds),
    min_samples: Number(form.min_samples),
    cooldown_seconds: Number(form.cooldown_seconds),
    enabled: form.enabled,
  }
}

export function Autoscale() {
  const qc = useQueryClient()
  const [form, setForm] = useState<PolicyForm>(defaultForm)
  const [editingID, setEditingID] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api<AutoscaleOverview>('/v1/autoscale/overview'),
    staleTime: 10_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
  }

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiWrite<ScalingPolicy>('/v1/autoscale/policies', 'POST', body),
    onSuccess: (p) => {
      setNotice(`สร้างนโยบาย ${p.name} แล้ว`)
      setEditingID('')
      setForm(defaultForm())
      refresh()
    },
    onError: (err) => setError(errorText(err, 'สร้างนโยบายไม่สำเร็จ')),
  })

  const update = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      apiWrite<ScalingPolicy>(`/v1/autoscale/policies/${input.id}`, 'PUT', input.body),
    onSuccess: (p) => {
      setNotice(`บันทึกนโยบาย ${p.name} แล้ว`)
      setEditingID('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'บันทึกนโยบายไม่สำเร็จ')),
  })

  const archive = useMutation({
    mutationFn: (id: string) => apiWrite<{ status: string }>(`/v1/autoscale/policies/${id}`, 'DELETE'),
    onSuccess: () => {
      setNotice('เก็บนโยบายเป็นประวัติแล้ว · รายการตัดสินใจยังอยู่')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'เก็บนโยบายไม่สำเร็จ')),
  })

  const evaluate = useMutation({
    mutationFn: (id: string) => apiWrite<ScalingEvaluation>(`/v1/autoscale/policies/${id}/evaluate`, 'POST', {}),
    onSuccess: (res) => {
      setNotice(res.recommendation)
      refresh()
    },
    onError: (err) => setError(errorText(err, 'ประเมินนโยบายไม่สำเร็จ')),
  })

  const scaleOut = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ worker: ScalingWorker; decision: ScalingDecision }>(
        `/v1/autoscale/policies/${id}/scale-out`,
        'POST',
        { reason: 'เจ้าของสั่งขยายจากหน้าจอ autoscale' },
      ),
    onSuccess: (res) => {
      setNotice(`เริ่ม worker pid ${res.worker.pid} บนพอร์ต ${res.worker.port} แล้ว`)
      refresh()
    },
    onError: (err) => setError(errorText(err, 'เริ่ม worker ไม่สำเร็จ')),
  })

  const scaleIn = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ worker: ScalingWorker; decision: ScalingDecision }>(
        `/v1/autoscale/policies/${id}/scale-in`,
        'POST',
        { reason: 'เจ้าของสั่งลดจำนวนจากหน้าจอ autoscale' },
      ),
    onSuccess: (res) => {
      setNotice(`หยุด worker pid ${res.worker.pid} แล้ว · ไม่ได้แตะ API หลักบน 8787/8790`)
      refresh()
    },
    onError: (err) => setError(errorText(err, 'หยุด worker ไม่สำเร็จ')),
  })

  const data = overview.data
  const policies = data?.policies ?? []
  const decisions = data?.decisions ?? []
  const workers = data?.workers ?? []
  const runningWorkers = workers.filter((w) => w.status === 'running' || w.status === 'starting').length
  const lastAction = decisions.find((d) => d.action !== 'hold')

  const submit = () => {
    setError(null)
    const body = payload(form)
    if (editingID) update.mutate({ id: editingID, body })
    else create.mutate(body)
  }

  const startEdit = (p: ScalingPolicy) => {
    setEditingID(p.id)
    setForm(formFromPolicy(p))
    setNotice(`กำลังแก้ไข ${p.name}`)
  }

  const cancelEdit = () => {
    setEditingID('')
    setForm(defaultForm())
    setNotice(null)
  }

  return (
    <div className="stack">
      <PageHead
        title="การขยายขนาดอัตโนมัติ"
        sub="ตัดสินใจจากตัวอย่าง CPU และ p95 latency จริง · คำแนะนำถูกบันทึกก่อนเสมอ · โปรเซสใหม่บนพอร์ต 8899 ต้องให้เจ้าของสั่ง"
        actions={
          <button className="btn btn-sm" onClick={refresh} disabled={overview.isFetching}>
            {overview.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status">
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}
      {error ? <div className="form-error">{error}</div> : null}

      <div className="grid-4 stagger">
        <Stat label="นโยบายที่ใช้งาน" value={number(policies.length)} unit="รายการ" foot="ไม่รวมนโยบายที่เก็บถาวร" />
        <Stat label="Replica ปัจจุบัน" value={number((data?.baseline_replicas ?? 1) + runningWorkers)} unit="ตัว"
          foot={`พื้นฐาน ${data?.baseline_replicas ?? 1} · worker ${runningWorkers}`} />
        <Stat label="Worker บนพอร์ต 8899" value={number(runningWorkers)} unit="ตัว"
          foot={runningWorkers > 0 ? 'มีโปรเซสเสริมทำงานอยู่' : 'ยังไม่มีโปรเซสเสริม'} />
        <Stat label="การตัดสินใจล่าสุด" value={lastAction ? ACTION_LABEL[lastAction.action] : 'ยังไม่มี'}
          foot={lastAction ? REASON_LABEL[lastAction.reason] ?? lastAction.reason : 'ประเมินนโยบายเพื่อเริ่มบันทึก'} />
      </div>

      <div className="card card-pad">
        <div className="card-label">ขอบเขตของระบบ</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '10px 0 0' }}>
          {data?.note ?? 'ระบบบันทึกคำแนะนำก่อนเสมอ · การเริ่มหรือหยุดโปรเซสเกิดเฉพาะเมื่อเจ้าของสั่ง และจำกัดเฉพาะพอร์ต 8899'}
        </p>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          <span className="chip gold">API หลัก · 8787 / 8790</span>
          <span className="chip">Worker ที่จัดการได้ · {data?.worker_port ?? 8899}</span>
          <span className={`chip ${data?.worker_script_ready ? 'chip-on' : ''}`}>
            {data?.worker_script_ready ? 'พบสคริปต์ scale-worker.sh' : 'ไม่พบสคริปต์ scale-worker.sh'}
          </span>
        </div>
      </div>

      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="card-label">นโยบาย</div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            CPU เป็นค่าเฉลี่ยในช่วงหน้าต่าง · p95 อ่านจาก trace จริง · ช่วงห่างระหว่างเกณฑ์ขยายและลดคือ hysteresis
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          {overview.isLoading ? (
            <div style={{ padding: 16 }}><Skel h={180} /></div>
          ) : overview.isError ? (
            <div className="form-error" style={{ margin: 16 }}>
              {errorText(overview.error, 'โหลดนโยบาย autoscale ไม่สำเร็จ')}
            </div>
          ) : policies.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีนโยบาย autoscale"
                body="สร้างนโยบายแรกจากแบบฟอร์มด้านล่าง · ค่าเริ่มต้นคือ 1 ถึง 2 replica, ขยายที่ CPU 70% และลดที่ 35%"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th>นโยบาย</th>
                  <th style={{ width: 110 }}>Replica</th>
                  <th style={{ width: 160 }}>CPU</th>
                  <th style={{ width: 170 }}>p95 latency</th>
                  <th style={{ width: 120 }}>Cooldown</th>
                  <th style={{ width: 110 }}>สถานะ</th>
                  <th style={{ width: 300 }}>การทำงาน</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ fontSize: 12.5 }}>{p.name}</div>
                      <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        {p.service} · หน้าต่าง {p.sample_window_seconds} วินาที · อย่างน้อย {p.min_samples} ตัวอย่าง
                      </div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        อัปเดต {thaiDateTime(p.updated_at)} · version {p.version}
                      </div>
                    </td>
                    <td className="tnum">
                      <div>{p.current_replicas} / {p.max_replicas}</div>
                      <div className="dim" style={{ fontSize: 10.5 }}>ขั้นต่ำ {p.min_replicas}</div>
                    </td>
                    <td>
                      <div className="tnum" style={{ fontSize: 12 }}>เป้าหมาย {p.target_cpu_pct}%</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        ขยาย ≥ {p.cpu_scale_out_pct}% · ลด ≤ {p.cpu_scale_in_pct}%
                      </div>
                    </td>
                    <td>
                      <div className="tnum" style={{ fontSize: 12 }}>เป้าหมาย {p.target_p95_ms} ms</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        ขยาย ≥ {p.p95_scale_out_ms} · ลด ≤ {p.p95_scale_in_ms}
                      </div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>
                        {p.latency_route ? `เส้นทาง ${p.latency_route}` : 'ทุกเส้นทาง'}
                      </div>
                    </td>
                    <td className="tnum" style={{ fontSize: 12 }}>{p.cooldown_seconds} วินาที</td>
                    <td>
                      <span className={`sev ${p.enabled ? 'sev-low' : 'sev-medium'}`}>
                        <i className="sev-dot" />
                        {p.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                      </span>
                      {p.last_decision_at ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                          ตัดสินใจล่าสุด {thaiDateTime(p.last_decision_at)}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm"
                          disabled={evaluate.isPending || !p.enabled}
                          onClick={() => { setError(null); evaluate.mutate(p.id) }}
                        >
                          ประเมินเดี๋ยวนี้
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={scaleOut.isPending || runningWorkers > 0 || p.current_replicas >= p.max_replicas}
                          onClick={() => {
                            if (!window.confirm(`เริ่มโปรเซสใหม่บนพอร์ต 8899 สำหรับ ${p.name}?`)) return
                            setError(null)
                            scaleOut.mutate(p.id)
                          }}
                        >
                          ขยายเพิ่ม
                        </button>
                        <button
                          className="btn btn-sm"
                          disabled={scaleIn.isPending || p.live_workers === 0 || p.current_replicas <= p.min_replicas}
                          onClick={() => {
                            if (!window.confirm(`หยุด worker ของ ${p.name}? API หลักบน 8787/8790 จะไม่ถูกแตะต้อง`)) return
                            setError(null)
                            scaleIn.mutate(p.id)
                          }}
                        >
                          ลดจำนวน
                        </button>
                        <button className="btn btn-sm btn-ghost" onClick={() => startEdit(p)}>แก้ไข</button>
                        <button
                          className="btn btn-sm btn-ghost"
                          disabled={archive.isPending || p.live_workers > 0}
                          title={p.live_workers > 0 ? 'หยุด worker ก่อนเก็บนโยบาย' : undefined}
                          onClick={() => {
                            if (!window.confirm(`เก็บนโยบาย ${p.name} เป็นประวัติ?`)) return
                            setError(null)
                            archive.mutate(p.id)
                          }}
                        >
                          เก็บถาวร
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card card-pad">
        <div className="card-label">{editingID ? 'แก้ไขนโยบาย' : 'สร้างนโยบายใหม่'}</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
          เป้าหมาย CPU และ p95 ใช้เป็นตัวหารของสูตร replica · เกณฑ์ขยายต้องไม่ต่ำกว่าเป้าหมาย และเกณฑ์ลดต้องต่ำกว่าเป้าหมาย
          เพื่อให้ช่วง hysteresis มีผลจริง
        </p>

        <div className="grid-3" style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="as-name">ชื่อนโยบาย</label>
            <input id="as-name" className="input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-service">บริการ</label>
            <input id="as-service" className="input" value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-route">เส้นทาง p95 (ว่าง = ทุกเส้นทาง)</label>
            <input id="as-route" className="input" placeholder="/v1/live" value={form.latency_route}
              onChange={(e) => setForm({ ...form, latency_route: e.target.value })} />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="as-min">จำนวนต่ำสุด</label>
            <input id="as-min" className="input" type="number" min={1} max={50} value={form.min_replicas}
              onChange={(e) => setForm({ ...form, min_replicas: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-max">จำนวนสูงสุด</label>
            <input id="as-max" className="input" type="number" min={1} max={50} value={form.max_replicas}
              onChange={(e) => setForm({ ...form, max_replicas: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-window">หน้าต่างตัวอย่าง (วินาที)</label>
            <input id="as-window" className="input" type="number" min={30} max={86400} value={form.sample_window_seconds}
              onChange={(e) => setForm({ ...form, sample_window_seconds: e.target.value })} />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="as-target-cpu">เป้าหมาย CPU (%)</label>
            <input id="as-target-cpu" className="input" type="number" min={1} max={100} value={form.target_cpu_pct}
              onChange={(e) => setForm({ ...form, target_cpu_pct: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-out-cpu">ขยายเมื่อ CPU ≥ (%)</label>
            <input id="as-out-cpu" className="input" type="number" min={1} max={100} value={form.cpu_scale_out_pct}
              onChange={(e) => setForm({ ...form, cpu_scale_out_pct: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-in-cpu">ลดเมื่อ CPU ≤ (%)</label>
            <input id="as-in-cpu" className="input" type="number" min={0} max={100} value={form.cpu_scale_in_pct}
              onChange={(e) => setForm({ ...form, cpu_scale_in_pct: e.target.value })} />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="as-target-p95">เป้าหมาย p95 (ms)</label>
            <input id="as-target-p95" className="input" type="number" min={1} max={600000} value={form.target_p95_ms}
              onChange={(e) => setForm({ ...form, target_p95_ms: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-out-p95">ขยายเมื่อ p95 ≥ (ms)</label>
            <input id="as-out-p95" className="input" type="number" min={1} max={600000} value={form.p95_scale_out_ms}
              onChange={(e) => setForm({ ...form, p95_scale_out_ms: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-in-p95">ลดเมื่อ p95 ≤ (ms)</label>
            <input id="as-in-p95" className="input" type="number" min={0} max={600000} value={form.p95_scale_in_ms}
              onChange={(e) => setForm({ ...form, p95_scale_in_ms: e.target.value })} />
          </div>
        </div>

        <div className="grid-3">
          <div className="field">
            <label htmlFor="as-samples">จำนวนตัวอย่างขั้นต่ำ</label>
            <input id="as-samples" className="input" type="number" min={1} max={1000} value={form.min_samples}
              onChange={(e) => setForm({ ...form, min_samples: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-cooldown">Cooldown (วินาที)</label>
            <input id="as-cooldown" className="input" type="number" min={0} max={86400} value={form.cooldown_seconds}
              onChange={(e) => setForm({ ...form, cooldown_seconds: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="as-enabled">สถานะ</label>
            <select id="as-enabled" className="select" value={form.enabled ? 'on' : 'off'}
              onChange={(e) => setForm({ ...form, enabled: e.target.value === 'on' })}>
              <option value="on">เปิดใช้งาน</option>
              <option value="off">ปิดใช้งาน</option>
            </select>
          </div>
        </div>

        <div className="row" style={{ gap: 12, marginTop: 18 }}>
          <button className="btn btn-primary" onClick={submit} disabled={create.isPending || update.isPending}>
            {editingID ? (update.isPending ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข') : (create.isPending ? 'กำลังสร้าง…' : 'สร้างนโยบาย')}
          </button>
          {editingID ? <button className="btn btn-ghost" onClick={cancelEdit}>ยกเลิก</button> : null}
        </div>
      </div>

      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="card-label">รายการตัดสินใจล่าสุด</div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ทุกครั้งที่ประเมินจะบันทึกหนึ่งแถว · แถวที่ถูก cooldown ระงับจะแสดง would_action และเครื่องหมายระงับ
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          {overview.isLoading ? (
            <div style={{ padding: 16 }}><Skel h={220} /></div>
          ) : decisions.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty title="ยังไม่มีการตัดสินใจ" body="กด “ประเมินเดี๋ยวนี้” เพื่ออ่าน CPU และ p95 จากตัวอย่างจริงแล้วบันทึกคำแนะนำ" />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>เวลา</th>
                  <th>นโยบาย</th>
                  <th style={{ width: 100 }}>ผล</th>
                  <th style={{ width: 210 }}>เหตุผล</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Replica</th>
                  <th style={{ width: 120, textAlign: 'right' }}>CPU</th>
                  <th style={{ width: 140, textAlign: 'right' }}>p95</th>
                  <th style={{ width: 130 }}>ที่มา</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d) => (
                  <tr key={d.id} style={{ cursor: 'default' }}>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(d.decided_at)}</td>
                    <td>
                      <div style={{ fontSize: 12.5 }}>{d.policy_name || d.policy_id}</div>
                      {d.note ? <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{d.note}</div> : null}
                    </td>
                    <td>
                      <span className={actionTone(d.action)}>{ACTION_LABEL[d.action]}</span>
                      {d.suppressed ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                          ระงับ · อยากทำ {ACTION_LABEL[d.would_action]}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div style={{ fontSize: 11.5 }}>{REASON_LABEL[d.reason] ?? d.reason}</div>
                      {d.cooldown_remaining_seconds > 0 ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          เหลือ cooldown {d.cooldown_remaining_seconds} วินาที
                        </div>
                      ) : null}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {d.current_replicas} → {d.target_replicas}
                      <div className="dim" style={{ fontSize: 10.5 }}>สูตรขอ {d.desired_replicas}</div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {d.cpu_pct.toFixed(2)}%
                      <div className="dim" style={{ fontSize: 10.5 }}>{d.cpu_samples} ตัวอย่าง</div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {d.latency_available ? `${d.p95_latency_ms.toFixed(1)} ms` : 'ไม่มีข้อมูล'}
                      <div className="dim" style={{ fontSize: 10.5 }}>
                        {d.latency_samples} span · เกณฑ์ {d.p95_threshold_ms}
                      </div>
                    </td>
                    <td>
                      <span className="chip">{d.source === 'owner' ? 'เจ้าของสั่ง' : 'ระบบประเมิน'}</span>
                      {d.applied ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>ดำเนินการแล้ว</div>
                      ) : (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>คำแนะนำ</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="card-label">Worker บนพอร์ต 8899</div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ระบบเริ่มและหยุดเฉพาะโปรเซสที่ลงทะเบียนในตารางนี้ · ไม่มีคำสั่งใดแตะ 8787 หรือ 8790
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          {overview.isLoading ? (
            <div style={{ padding: 16 }}><Skel h={160} /></div>
          ) : workers.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty title="ยังไม่มี worker" body="เมื่อเจ้าของกด “ขยายเพิ่ม” ระบบจะเริ่ม API โปรเซสที่สองบน 8899 พร้อมไฟล์ log แยก" />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th>Worker</th>
                  <th style={{ width: 120 }}>สถานะ</th>
                  <th style={{ width: 100, textAlign: 'right' }}>PID</th>
                  <th style={{ width: 90, textAlign: 'right' }}>พอร์ต</th>
                  <th style={{ width: 200 }}>เริ่ม / หยุด</th>
                  <th>Log</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.id} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ fontSize: 12.5 }}>{w.name}</div>
                      <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{w.policy_id}</div>
                    </td>
                    <td>
                      <span className={workerTone(w.status)}>
                        <i className="sev-dot" />
                        {WORKER_LABEL[w.status]}
                      </span>
                      {w.last_error ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>{w.last_error}</div>
                      ) : null}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{w.pid || '—'}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{w.port}</td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      <div>เริ่ม {w.started_at ? thaiDateTime(w.started_at) : '—'}</div>
                      <div style={{ marginTop: 3 }}>หยุด {w.stopped_at ? thaiDateTime(w.stopped_at) : '—'}</div>
                    </td>
                    <td className="mono" style={{ fontSize: 10.5, wordBreak: 'break-all' }}>{w.log_path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
